import * as React from "react";
import { keyboardOpen } from "@/hooks/useChatViewport";

export type KeyboardBox = { top: number; height: number };

/**
 * On a phone with the keyboard up, the part of the screen you can see is the
 * visual viewport (the top 40% or so), but a dialog laid out against the
 * whole layout sits half behind the keyboard (Felipe, Oct 19: the decline
 * reason's note box and its Send button were under the keys). While the
 * keyboard is open this returns the visible box, and the dialog bases
 * (Dialog and AlertDialog) pin their content inside it and let it scroll
 * there. Null on desktop widths and whenever the keyboard is closed.
 */
export function useKeyboardBox(): KeyboardBox | null {
  const [box, setBox] = React.useState<KeyboardBox | null>(null);
  React.useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    const apply = () => {
      const open = window.innerWidth < 640 && keyboardOpen(window.innerHeight, vv.height);
      setBox(open ? { top: Math.round(vv.offsetTop), height: Math.round(vv.height) } : null);
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, []);
  return box;
}

/**
 * The inline style that pins overlay content inside the visible box.
 * A sheet hangs from the top of the keyboard; a centred box moves to the top
 * of the visible area (it keeps its horizontal centring).
 */
export function keyboardPinStyle(kb: KeyboardBox | null, shape: "sheet" | "centered" = "sheet"): React.CSSProperties | undefined {
  if (!kb) return undefined;
  if (shape === "sheet") {
    const bottom = Math.max(0, window.innerHeight - (kb.top + kb.height));
    return { top: "auto", bottom, maxHeight: kb.height - 8, transform: "none" };
  }
  return { top: kb.top + 8, maxHeight: kb.height - 16, transform: "translateX(-50%)" };
}

/**
 * Keeps the field being typed in on screen: when the keyboard box appears or
 * moves, and whenever focus moves inside the content while it is open, the
 * focused element is scrolled into view inside the content (its scroll
 * padding keeps it clear of the sticky header and footer).
 */
export function useKeepFocusedFieldVisible(content: React.RefObject<HTMLElement | null>, kb: KeyboardBox | null) {
  const reveal = React.useCallback(() => {
    const el = typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    if (!el || !content.current || !content.current.contains(el)) return;
    if (typeof el.scrollIntoView !== "function") return; // jsdom
    const show = () => el.scrollIntoView({ block: "nearest" });
    // Now, and again once the sheet has settled (iOS resizes the visible
    // area in steps while the keyboard animates in).
    requestAnimationFrame(show);
    window.setTimeout(show, 300);
  }, [content]);
  React.useEffect(() => {
    if (kb) reveal();
  }, [kb?.top, kb?.height, kb, reveal]);
  return kb ? reveal : undefined;
}
